import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, input, model, signal } from '@angular/core';

import { Observable, distinctUntilChanged, filter, map } from 'rxjs';

import { InputChoicesComponent } from '@camelot/form-input';
import { InputChoices } from '@camelot/form-model';
import { FontIconComponent } from '@camelot/icons';
import { HandleSimpleRequest } from '@camelot/server';
import { Address, AddressType, CamContactService, Contact } from '@camelot/services';
import { TranslatePipe } from '@camelot/translation';
import {
  ButtonComponent,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  CardTitleComponent,
  EmptyComponent,
  ErrorComponent,
  LoaderComponent,
  TitleComponent,
} from '@camelot/ui';
import { CamBaseComponent, extractSingleValue, fullName, isNonNullable } from '@camelot/utils';

import { CamTranslationCore } from '../../translation.service';
import { ContactProfileComponent } from './profile/contact-profile.component';

export interface ContactProfileOptions {
  withVATNumber?: boolean;
  withInvoicingAddress?: boolean;
}

@Component({
  selector: 'cam-contact-profile-card',
  imports: [
    AsyncPipe,
    ButtonComponent,
    CardComponent,
    CardContentComponent,
    CardHeaderComponent,
    CardTitleComponent,
    ContactProfileComponent,
    EmptyComponent,
    ErrorComponent,
    FontIconComponent,
    InputChoicesComponent,
    LoaderComponent,
    TitleComponent,
    TranslatePipe,
  ],
  templateUrl: './contact-profile-card.component.html',
  styleUrl: './contact-profile-card.component.scss',
})
export class ContactProfileCardComponent extends CamBaseComponent {
  contactId = model.required<string>();

  navigationLink = input<string | null>(null);

  editMode = input(false);

  options = input<ContactProfileOptions>({});

  private readonly _contactsService = inject(CamContactService);

  public readonly contact$ = signal<Observable<Contact | null> | null>(null);
  public readonly isEditing = signal<boolean>(false);
  public readonly selectedId = signal<string | null>(null);
  public contactPreferenceById = new HandleSimpleRequest<Contact | null>();
  public contactSelect: InputChoices | null = null;

  constructor() {
    super();
    CamTranslationCore.getInstance();
  }

  ngOnInit(): void {
    this.selectedId.set(this.contactId() ?? null);
    this.contact$.set(this.contactPreferenceById.get$());
    this._fetch(this.contactId());
  }

  public navigate() {
    const navigationLink = this.navigationLink();
    if (navigationLink) {
      this._router.navigateByUrl(navigationLink);
    }
  }

  public getInvoicingAddress(contact: Contact): Address | null {
    return contact.contactAddresses?.find(ca => ca.types.includes(AddressType.Invoicing))?.address ?? null;
  }

  public startEditing() {
    this._buildSelector();
    this.isEditing.set(true);
  }

  private _buildSelector() {
    const currentId = this.selectedId();
    const toContactOption = (contact: Contact) => ({
      data: contact,
      id: contact.id,
      name: fullName({ firstName: contact.firstName, name: contact.lastName }),
    });

    this.contactSelect = new InputChoices({
      advancedSearch$: (search?: string | null, selectId?: string[]) => {
        return this._contactsService.fetchSearchContact$(search ?? '', selectId).pipe(
          filter(isNonNullable),
          map(contacts => contacts.map(toContactOption))
        );
      },
      key: 'contactId',
      multiple: false,
      value: currentId ? [currentId] : [],
      withSearch: true,
    });

    this._registerSubscription(
      this.contactSelect.changeValue$
        .pipe(
          map(value => extractSingleValue(value)),
          distinctUntilChanged()
        )
        .subscribe(newId => {
          this.isEditing.set(false);
          if (!newId || newId === this.selectedId()) {
            return;
          }
          this.selectedId.set(newId);
          this._fetch(newId);
          this.contactId.set(newId);
        })
    );
  }

  private _fetch(id: string | null) {
    if (!id) {
      return;
    }
    const vatNumberProps: Array<keyof Contact> = this.options().withVATNumber ? ['vatNumber'] : [];
    const invoicingAddressProps: Array<keyof Contact> = this.options().withInvoicingAddress ? ['contactAddresses'] : [];
    const props: Array<keyof Contact> = [
      'firstName',
      'lastName',
      'mail',
      'phoneNumber',
      ...vatNumberProps,
      ...invoicingAddressProps,
    ];

    this.requestState.asked();
    this._registerSubscription(
      this.contactPreferenceById.fetch(this._contactsService.fetchDynamicProps$(id, props)).subscribe({
        complete: () => this.requestState.completed(),
        error: (error: HttpErrorResponse) => {
          this.requestState.onError(error.status, error.statusText);
        },
      })
    );
  }
}
